<template>
  <div>
    <component
      :title="slideoverTitle"
      :is="slideoverComponent"
      :visible="slideoverVisible"
      v-on:toggle-slideover="closeSlideover"
      v-on:update-grid-notes="updateGridNotes"
      :orders="selectedRows"
    />

    <Alert
      v-if="errorGrid"
      v-on:close="closeAlert"
      header="Error"
      type="error"
      :error="errorGrid"
    />

    <MainHeader>
      <div class="lg:flex lg:items-center lg:justify-between">
        <!-- Left side -->
        <div class="flex-1 min-w-0">
          <div class="flex items-center pr-4 sm:truncate">
            <h1
              class="text-2xl font-bold leading-7 text-cool-gray-900 sm:leading-9"
            >
              <span> Backorder Status </span>
            </h1>
          </div>
        </div>

        <!-- Right side  -->
        <div class="mt-5 flex lg:mt-0 lg:ml-4 space-x-2">
          <Button
            class="btn btn-primary"
            :disabled="!hasRowsSelected"
            v-on:click-handler="openSlideover()"
          >
            Add Notes
          </Button>
        </div>
      </div>
    </MainHeader>

    <div class="grid grid-cols-12 gap-2 text-sm m-4">
      <!-- Left Column -->
      <div class="flex flex-col col-span-2 space-y-2">
        <!-- Form -->
        <div class="max-w-full border-b border-gray-200 rounded-lg shadow">
          <div
            class="px-6 py-2 text-gray-500 bg-gray-50 border-b border-gray-200"
          >
            Search Orders
          </div>
          <div class="px-6 py-4 space-y-4 text-black bg-white">
            <div class="flex flex-row space-x-4">
              <div class="w-full">
                <form @submit.prevent="loadBackorders" class="space-y-4">
                  <div>
                    <label
                      class="block text-sm font-medium leading-5 text-gray-700"
                    >
                      Search Type
                    </label>
                    <div>
                      <select
                        class="mt-1 block form-select w-full py-1 px-2 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:shadow-outline-blue focus:border-blue-300 transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                        required="required"
                        v-model="searchType"
                      >
                        <option value="variant_id">Variant</option>
                        <option value="expected_ship_date"
                          >Expected Ship Date</option
                        >
                      </select>
                    </div>
                  </div>
                  <!-- Variant -->
                  <div>
                    <label
                      class="block text-sm font-medium leading-5 text-gray-700"
                    >
                      Search {{ formatedSearchType }}
                    </label>

                    <input
                      v-if="searchType === 'variant_id'"
                      v-model="filter"
                      type="number"
                      class="w-full from mt-1 form-input py-2 px-3 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:shadow-outline-blue focus:border-blue-300 transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                    />
                    <div v-else>
                      <div class="text-xs text-gray-500 py-1">
                        Enter today's date to review late orders or you can
                        search up to a week in the future
                      </div>
                      <v-date-picker
                        v-model="filter"
                        mode="single"
                        color="green"
                        :max-date="maxDate"
                      />
                    </div>
                  </div>
                  <div>
                    <Button
                      type="submit"
                      :disabled="!canSearch"
                      class="w-full flex justify-center"
                    >
                      <span>Search</span>
                      <LoadingSpinner class="ml-2 w-5 h-5" v-if="processing" />
                    </Button>
                  </div>
                  <div v-if="showDetails">
                    <div
                      class="block text-sm font-medium leading-5 text-gray-700"
                    >
                      You've selected the:
                      <div class="font-bold">{{ productName }}</div>
                      <div>{{ variantName }}</div>
                    </div>
                  </div>
                </form>
              </div>
            </div>
            <HttpError :error="error" dismissable />
          </div>
        </div>
      </div>
      <div class="flex flex-col col-span-10 space-y-2">
        <div class="flex m-4 overflow-x-scroll">
          <ag-grid-vue
            id="order_tracking"
            style="width: 100%; height: 800px"
            class="ag-theme-balham"
            :columnDefs="columnDefs"
            :defaultColDef="defaultColDef"
            :rowData="rowData"
            :paginationAutoPageSize="false"
            :paginationPageSize="paginationPageSize"
            :pagination="true"
            :gridOptions="gridOptions"
            :context="context"
            :frameworkComponents="frameworkComponents"
            :enableCellTextSelection="true"
            :ensureDomOrder="true"
            :singleClickEdit="false"
            @selection-changed="onSelectionChanged"
            @grid-ready="onGridReady"
          >
          </ag-grid-vue>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import { AgGridVue } from "ag-grid-vue";
import agGrid from "@/mixins/agGrid.js";
import { ApiFactory } from "@/api/ApiFactory.js";
import Button from "@/components/elements/Button.vue";
import { date, linkOrder } from "@/components/agGrid/cellRenderers";
import MainHeader from "@/components/elements/MainHeader.vue";
import AddNotesSlideover from "./components/slideovers/AddNotes.vue";
import CheckedCircle from "./components/CellRender/CheckedCircle.vue";

const Utilities = ApiFactory.get("utilities");
const Inventories = ApiFactory.get("inventories");
import dateFormatter from "@/mixins/dateFormatter";

export default {
  name: "BackordersStatus",
  data() {
    return {
      error: null,
      errorGrid: null,
      processing: false,
      columnDefs: null,
      rowData: [],
      paginationPageSize: 100,
      frameworkComponents: null,
      gridApi: null,
      columnApi: null,
      gridOptions: null,
      context: null,
      defaultColDef: null,
      selectedRows: [],
      statuses: ["", "Pending", "Resolved", "(other)"],
      slideoverComponent: null,
      slideoverTitle: null,
      slideoverVisible: false,
      showCreatedDiscount: false,
      filter: null,
      variant: {},
      searchType: "variant_id"
    };
  },
  async created() {
    this.initColumnDefs();
  },
  mixins: [agGrid],
  components: {
    Button,
    AgGridVue,
    MainHeader,
    AddNotesSlideover,
    linkOrder,
    CheckedCircle // eslint-disable-line
  },
  computed: {
    hasRowsSelected() {
      return this.selectedRows.length;
    },
    showDetails() {
      return (
        Object.keys(this.variant).length > 0 && this.searchType === "variant_id"
      );
    },
    variantName() {
      return this.variant?.variant_name;
    },
    productName() {
      return this.variant?.product?.attributes?.name;
    },
    canSearch() {
      return this.filter && !this.processing;
    },
    formatedSearchType() {
      return this.searchType
        .replace(/_/g, " ")
        .split(" ")
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
    },
    maxDate() {
      const today = new Date();
      today.setDate(today.getDate() + 8);
      return today.toISOString().slice(0, 10);
    }
  },
  methods: {
    initColumnDefs() {
      this.columnDefs = [
        {
          headerName: "Variant",
          field: "variant_id",
          width: 150,
          checkboxSelection: true,
          headerCheckboxSelection: true,
          pinned: "left"
        },
        {
          headerName: "Product",
          field: "product_name",
          width: 150,
          pinned: "left"
        },
        {
          headerName: "Size",
          field: "size",
          width: 150,
          pinned: "left"
        },
        {
          headerName: "Order #",
          field: "SourceOrderNumber",
          pinned: "left",
          cellRendererFramework: "linkOrder"
        },
        {
          headerName: "Site",
          field: "CartName",
          filter: true
        },
        {
          headerName: "Category",
          field: "product_category",
          filter: true
        },
        {
          headerName: "Presell",
          field: "is_presell",
          valueGetter: params => {
            let isPresell = params.data.is_presell;
            if (isPresell > 0) return "True";
            return "False";
          },
          filterParams: this.customFilter({
            options: ["True", "False"]
          }),
          cellRendererFramework: "CheckedCircle",
          filter: true
        },
        {
          headerName: "Order Date",
          field: "OrderDate",
          cellRenderer: "dateCell",
          valueGetter: params => new Date(params.data.OrderDate),
          filter: "agDateColumnFilter",
          filterParams: {
            buttons: ["reset"],
            suppressAndOrCondition: true
          }
        },
        {
          headerName: "Expected Date",
          field: "ExpectedShipDate",
          cellRenderer: "dateCell",
          valueGetter: params => {
            return params.data.ExpectedShipDate
              ? new Date(params.data.ExpectedShipDate)
              : null;
          },
          filter: "agDateColumnFilter",
          filterParams: {
            buttons: ["reset"],
            suppressAndOrCondition: true
          }
        },

        {
          headerName: "Qty Needed",
          field: "QuantityNeeded",
          filter: "agNumberColumnFilter"
        },
        {
          headerName: "Expected",
          field: "Expected",
          filter: "agNumberColumnFilter"
        },
        {
          headerName: "Purchase Notes",
          colId: "purchaseNotes",
          field: "purchaseNotes",
          width: 400,
          cellStyle: {
            "white-space": "pre-wrap",
            "line-height": "normal",
            "wrap-text": "true"
          },
          editable: false,
          cellEditor: "agLargeTextCellEditor",
          valueSetter: this.editCells
        },
        {
          headerName: "Care Notes",
          colId: "careNotes",
          field: "careNotes",
          width: 400,
          cellStyle: { "white-space": "pre-wrap", "line-height": "normal" },
          editable: false,
          cellEditor: "agLargeTextCellEditor",
          valueSetter: this.editCells
        },
        {
          headerName: "Inventory Notes",
          colId: "inventoryNotes",
          field: "inventoryNotes",
          width: 400,
          cellStyle: { "white-space": "pre-wrap", "line-height": "normal" },
          editable: false,
          cellEditor: "agLargeTextCellEditor"
        }
      ];
    },
    async loadBackorders() {
      this.gridApi.showLoadingOverlay();
      this.variant = {};
      this.error = null;
      this.processing = true;
      let filter = this.filter;
      switch (this.searchType) {
        case "expected_ship_date": {
          filter = dateFormatter.formatDateForStorage(this.filter);
          break;
        }
        case "variant_id": {
          const response = await Inventories.getVariants({
            variant_ids: [this.filter],
            with: ["product"],
            hydrate: true
          });

          if (!response.data.body) {
            const error = new Error("Variant Not Found");
            error.response = {
              status: 422,
              data: { message: "Variant Not Found" }
            };
            this.rowData = [];
            this.processing = false;
            this.error = error;
            throw error;
          }
          this.variant = response.data.body[0];
          break;
        }
      }

      await Utilities.backorders({ [this.searchType]: filter })
        .then(response => {
          this.rowData = response.data.body;
        })
        .catch(error => {
          this.errorGrid = error;
        })
        .finally(() => {
          this.processing = false;
        });
    },
    onGridReady(params) {
      this.gridApi = params.api;
      this.columnApi = params.columnApi;
    },
    onSelectionChanged(event) {
      this.selectedRows = event.api.getSelectedRows();
    },

    getPurchaseNotes(params) {
      if (params.data?.Notes?.length) return params.data.Notes[0].Notes;
    },

    editCells(params) {
      const note = params.newValue;
      const colId = params.colDef.colId;
      const nodes = this.gridApi.getSelectedNodes();
      const data = {
        items: nodes.map(node => {
          return {
            OrderNumber: node.data.OrderNumber,
            ItemNumber: node.data.ItemNumber,
            variant_id: node.data.variant_id
          };
        }),
        [colId]: note
      };

      this.gridApi.showLoadingOverlay();

      Utilities.createBackordersNotes(data)
        .then(() => {
          this.updateGridNotes({
            [colId]: note
          });
        })
        .catch(error => {
          this.errorGrid = error;
        })
        .finally(() => {
          this.processing = false;
          this.gridApi.hideOverlay();
        });
    },

    formatNotes(existingNotes, newNotes) {
      let stamp = new Date().toISOString().slice(0, 10);
      stamp = `[${stamp}]: `;

      if (existingNotes?.trim()) {
        return `${stamp}${newNotes}\n${existingNotes}`;
      }

      return `${stamp}${newNotes}`;
    },

    updateGridNotes(params) {
      const nodes = this.gridApi.getSelectedNodes();

      nodes.forEach(element => {
        if (params.purchaseNotes?.trim())
          element.data.purchaseNotes = this.formatNotes(
            element.data.purchaseNotes,
            params.purchaseNotes
          );
        if (params.careNotes?.trim())
          element.data.careNotes = this.formatNotes(
            element.data.careNotes,
            params.careNotes
          );
        if (params.updateShipDate)
          element.data.ExpectedShipDate = params.updateShipDate;
      });

      this.gridApi.resetRowHeights();
      this.gridApi.deselectAll();
      this.gridApi.refreshCells();
    },

    openSlideover() {
      this.slideoverComponent = "AddNotesSlideover";
      this.slideoverTitle = "Add Notes";

      setTimeout(() => {
        this.slideoverVisible = true;
      }, 5);
    },

    closeSlideover() {
      this.slideoverVisible = false;

      setTimeout(() => {
        this.slideoverComponent = null;
      }, 500);
    },

    closeAlert() {
      this.errorGrid = null;
    }
  },
  beforeMount() {
    this.gridOptions = {
      headerHeight: 30,
      rowHeight: 200,
      pagination: true,
      rowSelection: "multiple",
      onSelectionChanged: this.onNoteSelectionChanged,
      rowDeselection: true
    };

    this.context = { componentParent: this };
    this.defaultColDef = {
      sortable: true,
      filter: true,
      suppressMovable: true,
      width: 100,
      wrapText: true,
      autoHeight: true
    };

    this.frameworkComponents = {
      dateCell: date
    };
  },
  filters: {}
};
</script>
<style scoped lang="scss">
@import "../../../node_modules/ag-grid-community/dist/styles/ag-grid.css";
@import "../../../node_modules/ag-grid-community/dist/styles/ag-theme-balham.css";
</style>
